import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import type { ApiError } from '@gd/shared'
import { ApiClientError } from './client'

/**
 * Sign-in routes for app accounts (see packages/api/src/session.ts). They sit outside the typed
 * `ApiRoutes` contract because they set and clear HttpOnly cookies rather than return data.
 */

/** Matches MIN_PASSWORD_LENGTH on the server. */
export const MIN_PASSWORD_LENGTH = 12

async function post(path: string, body?: unknown): Promise<void> {
  let res: Response
  try {
    res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiClientError(
      'Could not reach the server. Check your connection and try again.',
      'network_error',
      0,
    )
  }
  if (res.ok) return
  let parsed: ApiError | null = null
  try {
    parsed = (await res.json()) as ApiError
  } catch {
    parsed = null
  }
  throw new ApiClientError(
    parsed?.error?.message ?? (res.statusText || 'Request failed'),
    parsed?.error?.code ?? `http_${res.status}`,
    res.status,
  )
}

/** POST /api/auth/login. Resolves once the session cookies are set. */
export function login(email: string, password: string): Promise<void> {
  return post('/api/auth/login', { email, password })
}

/** POST /api/auth/logout. Revokes the session and clears the cookies. */
export function logout(): Promise<void> {
  return post('/api/auth/logout')
}

/** POST /api/auth/password. Replaces the password (and clears the temporary-password flag). */
export function changePassword(password: string): Promise<void> {
  return post('/api/auth/password', { password })
}

/**
 * The `next` query param, if it is a same-origin path ("/a/sky/today?range=4w"), else "/".
 * Rejects absolute URLs, protocol-relative "//host" and "/\host" (which browsers treat as
 * "//host"), control characters, and the sign-in pages themselves so we never loop.
 */
export function safeNext(next: string | null | undefined): string {
  if (!next || next[0] !== '/') return '/'
  if (next[1] === '/' || next[1] === '\\') return '/'
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(next)) return '/'
  try {
    const base = 'https://gd.invalid'
    const url = new URL(next, base)
    if (url.origin !== base) return '/'
    if (url.pathname === '/login' || url.pathname === '/welcome') return '/'
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/'
  }
}

/**
 * The service worker keeps recent /api answers for offline use (vite.config.ts, cache "api").
 * Drop them on sign out so the next person on this device can't read them offline.
 */
export async function clearOfflineApiCache(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') await caches.delete('api')
  } catch {
    // No Cache Storage (private mode, tests): nothing was kept.
  }
}

/** Signs out, then lands on /login with an empty query cache. */
export function useSignOut() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [pending, setPending] = useState(false)

  const signOut = useCallback(async () => {
    setPending(true)
    try {
      await logout()
    } catch {
      // The cookies may already be gone (expired session); still leave the app.
    }
    // Leave the app first so no mounted query refetches into a 401 while the cache empties.
    await navigate('/login', { replace: true })
    queryClient.clear()
    await clearOfflineApiCache()
    setPending(false)
  }, [navigate, queryClient])

  return { signOut, pending }
}
